import React from 'react';
import { ArrowLeft, ArrowRight, Info } from '@phosphor-icons/react';

import { MarketShell } from '../../../design-system/components/shells';
import { Button, PageHeader, Pill } from '../../../design-system/components/desktop';
import { Stepper } from '../../../design-system/components/desktop/complex';
import { Icon } from '../../../design-system/components/Icon';
import { useViewport } from '../../../design-system/hooks/useViewport';
import { formatCurrency, mediaUrl } from '../../../utils/common';
import { routes } from '../../../routes/routes';

import './create-listing.css';

/**
 * /create-listing, the view. Pure: every input is a prop, every output a
 * callback. The container (`CreateListing.jsx`) owns the step state, the
 * validation, the one POST, the analytics event and the redirect.
 *
 * SHELL: MarketShell, the same chrome as the rest of the signed in app.
 *
 * It used to be `ConsoleShell`, on the reasoning that buying and managing are
 * two shells. In practice that put a left sidebar around the seller's tools and
 * made them read as a separate application. Creating a listing is not a
 * departure from Sleevr, it is a thing you do in it, and the seller arrives
 * here from the "New listing" button on their dashboard — which no longer has
 * a sidebar either. One chrome, so nothing changes shape as they move.
 *
 * THE STEPPER IS THE SHARED ONE, not a sixth hand-rolled copy. The old
 * `.progress-stepper` drew five divs, filled every reached circle with the cyan
 * gradient that is being removed, and painted every icon in hardcoded `white`,
 * so a reached-but-not-current step was white-on-near-white in light theme.
 * Progress is STATE, so the shared component paints it accent; nothing on this
 * page is ink except the Next and Publish commands.
 *
 * THE RAIL replaces `LookUpCard`, which drew its own `.lookup-card` with its
 * own stylesheet and, on the wizard, sat in a flex row beside the step. It is
 * sticky here, because the card being listed is the one thing the seller must
 * not lose track of while they move between steps.
 */

/**
 * FOUR STEPS, NOT FIVE. "Collection" is gone.
 *
 * It asked for a collection address and a handling time, and neither is a fact
 * about the CARD — they are facts about the seller, identical on every listing
 * they will ever make, and re-asked once per listing. They belong on the seller
 * dashboard, edited once. The two fields still reach the create-listing
 * payload: `CreateListing.jsx` resolves the address from the seller's saved
 * addresses and keeps the wizard's handling-time default, so nothing is
 * dropped. The dashboard's editable pair is `SellerDashboard.view.jsx`'s to
 * add and is not built yet.
 */
export const WIZARD_STEPS = [
  { id: 1, label: 'Card details' },
  { id: 2, label: 'Photos' },
  { id: 3, label: 'Price' },
  { id: 4, label: 'Review' },
];

/**
 * Wizard progress.
 *
 * This is the shared `Stepper` and nothing else. It was tempting to rebuild the
 * upstream template's numbered, clickable node, but the old page's circles were
 * not clickable either, so that would be new behaviour bolted onto a
 * conversion, and the shared component already carries the whole strip as one
 * `role="img"` with a spoken name plus real text labels under the dots, which
 * is more than the five hand-rolled divs ever did.
 */
function WizardProgress({ current }) {
  return (
    <div className="dsa-cl-steps">
      <Stepper
        steps={WIZARD_STEPS.map((step) => step.label)}
        current={current - 1}
        showLabels
        label={`Step ${current} of ${WIZARD_STEPS.length}: ${WIZARD_STEPS[current - 1]?.label}`}
      />
    </div>
  );
}

/** The sticky summary of the card being listed. */
function SummaryRail({ card, formData }) {
  return (
    <aside className="dsa-cl-rail" aria-label="Listing summary">
      <p className="dsa-cl-rail__title">Listing summary</p>

      {card?.product_name ? (
        <div className="dsa-cl-rail__card">
          {card.image_url ? (
            <img
              className="dsa-cl-rail__art"
              src={mediaUrl(card.image_url)}
              alt=""
              aria-hidden="true"
            />
          ) : (
            <span className="dsa-cl-rail__art" aria-hidden="true" />
          )}
          <div>
            <p className="dsa-cl-rail__kicker">{card.catalog_group}</p>
            <p className="dsa-cl-rail__name">{card.product_name}</p>
            <div className="dsa-cl-rail__tags">
              {card.rarity ? <Pill>{card.rarity}</Pill> : null}
              {card.card_number ? <Pill>{card.card_number}</Pill> : null}
            </div>
          </div>
        </div>
      ) : (
        <p className="dsk-hint" style={{ marginTop: '12px' }}>
          Pick a catalogue card on step one and it appears here.
        </p>
      )}

      <div className="dsa-cl-rail__rows">
        <div className="dsa-cl-rail-row">
          <span className="dsa-cl-rail-row__label">Condition</span>
          <span className="dsa-cl-rail-row__value">{formData.condition}</span>
        </div>
        <div className="dsa-cl-rail-row">
          <span className="dsa-cl-rail-row__label">Finish</span>
          <span className="dsa-cl-rail-row__value">{formData.finish}</span>
        </div>
        <div className="dsa-cl-rail-row">
          <span className="dsa-cl-rail-row__label">Quantity</span>
          <span className="dsa-cl-rail-row__value">{formData.quantity}</span>
        </div>
        <div className="dsa-cl-rail-row">
          <span className="dsa-cl-rail-row__label">Photos</span>
          <span className="dsa-cl-rail-row__value">{formData.images?.length || 0}</span>
        </div>
        <div className="dsa-cl-rail-row">
          <span className="dsa-cl-rail-row__label">Your price</span>
          <span className="dsa-cl-rail-row__value">{formatCurrency(formData.price)}</span>
        </div>
      </div>

      <p className="dsa-cl-rail__note">
        <Icon as={Info} size="sm" />
        <span>
          Sleevr ships nationwide. Listings are reviewed before they appear in the marketplace.
        </span>
      </p>
    </aside>
  );
}

export function CreateListingView({
  currentStep = 1,
  onPrev,
  onNext,
  stepContent,
  card,
  formData,
}) {
  const { isDesktop } = useViewport();

  return (
    <MarketShell
      title="New listing"
      breadcrumbs={[
        { label: 'Selling' },
        { label: 'Listings', to: routes.DASHBOARD },
        { label: 'New listing' },
      ]}
    >
      <PageHeader
        title="New listing"
        sub="Four steps. Find the card in the catalogue, describe it honestly, price it against the market."
      />

      {/* NO DRAFT BANNER. The wizard has never persisted a draft: `formData` is
          component state and a refresh loses it. A "picked up where you left
          off" line would be a promise the app cannot keep, so it is flagged
          rather than drawn. */}

      <WizardProgress current={currentStep} />

      {/* THE RAIL MOVES BELOW THE ACTIONS ON A PHONE, and only there. At 1024
          and up it is the second grid column, sticky beside the step panel. In
          one column it was landing BETWEEN the step and the Next button — about
          380px of summary card, five label/value rows and a note — so on all
          four steps advancing the wizard meant scrolling past a panel nobody
          asked for, and on step one that panel is mostly the empty "pick a
          catalogue card and it appears here" line. The actions row is the
          wizard's primary command and belongs adjacent to what it advances. */}
      <div className="dsa-cl-body">
        <div>{stepContent}</div>
        {isDesktop ? <SummaryRail card={card} formData={formData} /> : null}
      </div>

      <div className="dsa-cl-actions">
        <Button variant="ghost" onClick={onPrev} disabled={currentStep === 1}>
          <Icon as={ArrowLeft} size="sm" />
          Previous
        </Button>

        <div className="dsa-cl-actions__end">
          {currentStep < WIZARD_STEPS.length ? (
            <Button variant="primary" onClick={onNext}>
              Next
              <Icon as={ArrowRight} size="sm" />
            </Button>
          ) : null}
        </div>
      </div>

      {isDesktop ? null : (
        <div className="dsa-cl-rail-after">
          <SummaryRail card={card} formData={formData} />
        </div>
      )}
    </MarketShell>
  );
}

export default CreateListingView;
